import { Location } from "@/custom-types/Location";

export const normalizeGoogleGeocodeResponse = ({
  response,
  isUserLocation = true,
}: {
  response: google.maps.GeocoderResult | google.maps.places.PlaceResult;
  isUserLocation?: boolean;
}): Location => {
  const components = response.address_components;
  let city =
    getComponentValue(components, "locality") ||
    getComponentValue(components, "postal_town") ||
    getComponentValue(components, "administrative_area_level_3"); // Next best thing in lieu of a city.,
  const zip = getComponentValue(components, "postal_code");
  const country = getComponentValue(components, "country", true);
  const countryCode = getComponentValue(components, "country");
  const state = getComponentValue(
    components,
    "administrative_area_level_1",
    true,
  );
  const stateCode = getComponentValue(
    components,
    "administrative_area_level_1",
  );
  const streetName = getComponentValue(components, "route");
  const streetNumber = getComponentValue(components, "street_number");
  let sublocality = getComponentValue(components, "sublocality");

  // Some places are coming back from google with a sublocality but not a locality
  // If this happens, move the sublocality into the city position.
  // e.g.:  928 Brooklyn Avenue, Brooklyn, NY
  if (!city && sublocality) {
    city = sublocality;
    sublocality = "undefined";
  }

  const locationSlug =
    [sublocality, city, stateCode, countryCode].filter(Boolean).join("-") ||
    country;

  const location = {
    city: city,
    coordinates: {
      latitude: response.geometry?.location?.lat(),
      longitude: response.geometry?.location?.lng(),
    },
    country: country,
    countryCode: countryCode,
    country_code: countryCode,
    formattedAddress: response.formatted_address || "",
    formatted_location: getFormattedLocation({
      city,
      country,
      countryCode,
      state,
      stateCode,
      sublocality,
    }),
    isUserLocation: isUserLocation,
    latitude: response.geometry?.location?.lat(),
    longitude: response.geometry?.location?.lng(),
    placeId: response.place_id || "",
    postalCode: zip,
    slug: locationSlug?.toLowerCase(),
    state: state,
    stateCode: stateCode,
    state_code: stateCode,
    street: {
      name: streetName || "",
      number: streetNumber || "",
    },
    sublocality: sublocality,
    zip: zip,
  };

  return location;
};

const getFormattedLocation = (location: {
  sublocality?: string;
  city?: string;
  stateCode?: string;
  state?: string;
  countryCode?: string;
  country?: string;
}) => {
  const { sublocality, city, stateCode, state, countryCode, country } =
    location;

  if ((sublocality || city) && stateCode) {
    return [sublocality, city, stateCode].filter(Boolean).join(", ");
  } else if (state && countryCode) {
    return [state, countryCode].filter(Boolean).join(", ");
  } else {
    return country;
  }
};

const getComponentValue = (
  addressComponents: google.maps.GeocoderAddressComponent[] | undefined,
  type: string,
  useLongNameProperty = false,
) => {
  const addressComponent = addressComponents?.find((el) =>
    el.types.includes(type),
  );

  const name =
    useLongNameProperty === true
      ? addressComponent?.long_name
      : addressComponent?.short_name;

  return name || "";
};
